import { useState } from 'react';
import { toast } from 'react-toastify';
import { ExcelService } from '../services/excelService';
import { DocumentProcessorFactory } from '../services/document-proccesors/document-processor.factory';
import { getSpeciesIdExcelNameMap } from 'common/src/api/species';

export const useDocumentUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [columns, setColumns] = useState<{ name: string; uid: string }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uniqueSpeciesIds, setUniqueSpeciesIds] = useState<string[]>([]);
  const [speciesIdMap, setSpeciesIdMap] = useState<Record<string, number>>({});

  const handleFileRead = async (file: File, documentType: string) => {
    try {
      const jsonData = await ExcelService.readFile(file);
      if (jsonData.length > 0) {
        const speciesMap = await getSpeciesIdExcelNameMap();
        setSpeciesIdMap(speciesMap);

        const processor = DocumentProcessorFactory.getProcessor(documentType);
        // Ahora transformData es asíncrono
        const transformedData = await processor.transformData(jsonData);

        const columnHeaders = Object.keys(transformedData[0]).map((key) => ({
          name: key,
          uid: key,
        }));

        setColumns(columnHeaders);
        setPreviewData(transformedData.slice(0, 5));

        // Extract unique species_ids
        if (['market_data', 'settlements'].includes(documentType)) {
          const speciesNames = new Set<string>();

          // Para market_data, buscamos en el campo 'item'
          if (documentType === 'market_data') {
            transformedData.forEach((item: any) => {
              if (item.item) {
                speciesNames.add(item.item.toString().toUpperCase());
              }
            });
          }
          // Para settlements, buscamos en la columna original 'ESPECIE'
          else if (documentType === 'settlements') {
            // Primero verificamos si algún registro tiene la columna 'ESPECIE'

            const hasEspecieColumn = jsonData.some(
              (item) =>
                'ESPECIE' in item || 'Especie' in item || 'especie' in item
            );

            if (hasEspecieColumn) {
              jsonData.forEach((item) => {
                const especieValue =
                  item.ESPECIE || item.Especie || item.especie;
                if (especieValue) {
                  speciesNames.add(especieValue.toString().toUpperCase());
                }
              });
            }
            // Si no hay columna ESPECIE, intentamos con variedad en los datos transformados
          }

          // Convertir el Set a un array
          setUniqueSpeciesIds(Array.from(speciesNames));
        } else {
          setUniqueSpeciesIds([]);
        }
      }
    } catch (error) {
      toast.error(`Error en el archivo: ${(error as Error).message}`);
      setPreviewData([]);
      setColumns([]);
      setUniqueSpeciesIds([]);
      setSpeciesIdMap({});
    }
  };

  const handleUpload = async (
    documentType: string,
    monthsParam: number = 4,
    uploadMode: 'replace' | 'append' = 'replace'
  ) => {
    if (!file || !previewData.length) return false;

    setIsUploading(true);
    const toastId = toast.loading('Subiendo datos a la base de datos...');
    let result: any;

    try {
      const jsonData = await ExcelService.readFile(file);
      const processor = DocumentProcessorFactory.getProcessor(documentType);
      console.log('processor', processor);

      // Ahora transformData es asíncrono
      const transformedData = await processor.transformData(jsonData);
      console.log('transformedData', transformedData);

      // Convertir los uniqueSpeciesIds (nombres) a IDs numéricos
      let speciesIdsNumeric: number[] | undefined;

      if (documentType === 'market_data' && uniqueSpeciesIds.length > 0) {
        // Filtrar solo los que tienen ID en el mapa
        speciesIdsNumeric = uniqueSpeciesIds
          .map((name) => speciesIdMap[name])
          .filter((id) => id !== undefined);

        // Si no se encontraron IDs, registrar advertencia
        if (speciesIdsNumeric.length === 0) {
          console.warn(
            'No se encontraron IDs correspondientes para los nombres de especies'
          );
          speciesIdsNumeric = undefined;
        }
      }

      // Pasar las especies y el parámetro de meses al método de carga
      const uploadOptions = {
        monthsParam,
        // Ahora enviamos los IDs numéricos, no los nombres
        speciesIds: speciesIdsNumeric,
        mode: uploadMode,
      };

      result = await processor.upload(transformedData as any, uploadOptions);
      if (!result || result === false || result?.success === false || result?.response?.data?.error) {
        throw new Error(result?.error || 'Error al subir los datos');
      }

      const uploaded = result?.rowsUploaded;
      const discarded = result?.discardedByDate ?? 0;
      const replaced = result?.replaced;

      toast.update(toastId, {
        render: replaced?.date_from
          ? replaced.mode === 'append'
            ? `Datos agregados: ${(replaced.inserted_rows ?? uploaded)?.toLocaleString('es')} filas nuevas (período ${replaced.date_from} → ${replaced.date_to}, sin tocar lo existente).`
            : `Datos subidos: ${(replaced.inserted_rows ?? uploaded)?.toLocaleString('es')} filas. Se reemplazó el período ${replaced.date_from} → ${replaced.date_to} (${(replaced.deleted_rows ?? 0).toLocaleString('es')} filas anteriores).`
          : uploaded
          ? `Datos subidos correctamente: ${uploaded.toLocaleString('es')} filas`
          : 'Datos subidos correctamente',
        type: 'success',
        isLoading: false,
        autoClose: 8000,
      });

      // El filtro por meses descarta filas sin avisar: si quedaron fuera, se
      // informa explícitamente para que no parezca que se subieron.
      if (discarded > 0) {
        toast.warning(
          `Se ignoraron ${discarded.toLocaleString('es')} filas con fecha anterior al ${result.cutoffDate} (rango de ${result.months} meses). Si necesitás incluirlas, subí el archivo de nuevo con un rango mayor.`,
          { autoClose: false }
        );
      }
      return true;
    } catch (error) {
      toast.update(toastId, {
        render: `Error: ${
          (error instanceof Error && error.message) ||
          result?.response?.data?.error ||
          'Error al subir los datos'
        }`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreviewData([]);
    setColumns([]);
    setUniqueSpeciesIds([]);
    setSpeciesIdMap({});
  };

  return {
    file,
    setFile,
    previewData,
    columns,
    isUploading,
    uniqueSpeciesIds,
    handleFileRead,
    handleUpload,
    handleCancel,
  };
};
